import axios from 'axios';
import https from 'https';
import userData from '@PROJECT_ROOT/assets/responsive-frontend/js/services/userData';
import windowManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/windowManipulationService';

const isClientSideRendering = windowManipulationService.isBrowserEnvironment();
const axiosInstanceQueryParams = {};
if (isClientSideRendering && ADRIATIC_PARTNER_RESPONSIVE_SCHEME_HOST === `${window.location.protocol}//${window.location.hostname}`) {
    const params = new URLSearchParams(window.location.search);
    const fsSet = params.get('fs_set');
    if (fsSet) {
        axiosInstanceQueryParams.fs_set = fsSet;
    }
}
const apiHostname = isClientSideRendering ? window.location.hostname : ADRIATIC_RESPONSIVE_SCHEME_HOST.replace('https://', '');
const BASE_URL = `https://${apiHostname}/`;
const BASE_API_URL = `https://${apiHostname.replace(/^www\./, 'api.')}/`;
const RSB_SERVICE = `${BASE_URL}rsb-service/`;
const agent = new https.Agent({
    rejectUnauthorized: isClientSideRendering || false,
});
const axiosInstance = axios.create({
    timeout: 40000,
    httpsAgent: agent,
    params: axiosInstanceQueryParams,
});
let requestScopedHeaders = {};
axiosInstance.interceptors.request.use(
    async (config) => {
        if (isClientSideRendering) {
            const token = userData.getTokenFromLocalStorage();
            config.headers['Adriatic-Bearer'] = token || 0;
        }
        // Za svaki request inject-amo custom header-e ako postoje, trenutno se koristi za postavljanje X-Forwarded-For.
        if (Object.keys(requestScopedHeaders).length > 0) {
            config.headers = {
                ...config.headers,
                ...requestScopedHeaders,
            };
        }

        return config;
    },
    (error) => Promise.reject(error),
);
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(error),
);

export const setRequestScopedHeaders = (headers) => {
    requestScopedHeaders = headers;
};

export const clearRequestScopedHeaders = () => {
    requestScopedHeaders = {};
};

export const getAdriaticLang = () => {
    // TODO: #55289 Postavljen je defaultni jezik na "en" iako se izravno kroz site ne moze desit da jezik nije postavljen,
    // plan mi je ukloniti ovo nakon testiranja na produkciji
    const defaultLang = 'en';
    return isClientSideRendering && window.AppConfig ? window.AppConfig.lang : defaultLang;
};

const twoLetterISOLanguageCodeParameter = getAdriaticLang();

export const getAdriaticLangForDateFormats = () => {
    const defaultLang = 'enGB';
    const lang = isClientSideRendering && window.AppConfig ? window.AppConfig.lang : defaultLang;
    const langOverrides = {
        en: defaultLang,
        no: 'nn',
    };
    return langOverrides[lang] ?? lang;
};

export const setBearerTokenFromSSR = (token) => {
    axiosInstance.defaults.headers['Adriatic-Bearer'] = token || 0;
};

export const setUserAgentFromSSR = (userAgent) => {
    axiosInstance.defaults.headers['User-Agent'] = userAgent || null;
};

export const setBrowserCookieFromSSR = (userCookie) => {
    axiosInstance.defaults.headers.cookie = userCookie || '';
};

// Dodatni parametar langFromSSR koji dolazi preko symfony-a (ne šalje se prilikom poziva sa CSR-a).
export const apiFetchData = (endpointPath, urlParam, langFromSSR, queryParams) => {
    const completePath = `${RSB_SERVICE}${endpointPath}${langFromSSR || twoLetterISOLanguageCodeParameter}`;

    return axiosInstance.get(completePath + (urlParam || ''), {
        params: {
            ...axiosInstanceQueryParams,
            ...(queryParams || {}),
        },
    });
};

export const apiFetchDataWithoutLangISO = (endpointPath, urlParam) => {
    const completePath = `${RSB_SERVICE}${endpointPath}`;
    if (urlParam) {
        return axiosInstance.get(completePath + urlParam);
    }
    return axiosInstance.get(completePath);
};

export const apiFetchDataTwoLetterBefore = (endpointPath, queryParams) => {
    const completePath = `${RSB_SERVICE}${twoLetterISOLanguageCodeParameter}${endpointPath}`;
    return axiosInstance.get(completePath, {
        params: {
            ...axiosInstanceQueryParams,
            ...(queryParams || {}),
        },
    });
};

export const apiFetchDataWithoutRsbService = (endpointPath) => {
    const completePath = `${twoLetterISOLanguageCodeParameter}/${endpointPath}`;
    return axiosInstance.get(completePath);
};

export const apiFetchDataOutOfRSB = (endpointPath) => {
    const completePath = `${BASE_URL}${twoLetterISOLanguageCodeParameter}${endpointPath}`;
    return axiosInstance.get(completePath);
};

export const apiFetchDataRaw = (endpointPath) => axiosInstance.get(endpointPath);

export const apiPostData = (endpointPath, data) => {
    const completePath = `${RSB_SERVICE}${endpointPath}`;
    return axiosInstance.post(completePath, data);
};

export const apiPostIterableData = (endpointPath, data) => {
    const completePath = `${BASE_API_URL}frontend-marketing-tool/${endpointPath}`;
    return axiosInstance.post(completePath, data);
};

export const apiPostDataWithConfig = (endpointPath, data, config) => {
    const completePath = `${RSB_SERVICE}${endpointPath}`;
    return axiosInstance.post(completePath, data, config);
};

// Ova funkcija se koristi za slanje POST zahtjeva u responsiveFrontendBundle.
export const apiPostUserData = (endpointPath, data) => {
    const mainURL = window.location.origin;
    const completePath = `${mainURL}/${twoLetterISOLanguageCodeParameter}/${endpointPath}`;
    return axiosInstance.post(completePath, data);
};

export const apiPostDataWithLangISO = (endpointPath, data) => {
    const completePath = `${RSB_SERVICE}${endpointPath}${twoLetterISOLanguageCodeParameter}`;
    return axiosInstance.post(completePath, data);
};

export const getCurrentJobId = () => (isClientSideRendering && window.PageConfig ? window.PageConfig.jobVacancyId : 0);
